import { Route } from 'react-router-dom';
import type { RedirectHref } from '@zg-rentals/auth-react/legacy';
import { rentalsLoginOptions } from '@zg-rentals/login-zillow';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { Instrument } from '@zg-rentals/react-hooks';
import { ErrorPage } from '@zg-rentals/error-page-components';
import type { RouteWithAuthProps } from '@common/components/RouteWithAuth';
import { RenterBlockedUser } from './RenterBlockedUser';
import { isRenterBlocked, RenterAuth } from './RenterAuthProvider';

const APP_NAME = 'rent-payment-web';

const loginRedirectHref = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: APP_NAME,
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  ...rentalsLoginOptions,
});

const countedLoginRedirectHref: RedirectHref = (href, params) => {
  getGlobalMonitor()?.count({
    name: 'rent-payment-web renter auth-react migration logged_out_redirect',
  });
  return loginRedirectHref(href, params);
};

const RenterAuthError = () => (
  <Instrument
    onInitialRender={() =>
      getGlobalMonitor()?.count({
        name: 'rent-payment-web renter auth-react migration auth_fetch_error',
      })
    }
  >
    <ErrorPage
      errorMessage="Something went wrong on our end"
      errorDetails="Please try again."
      action={<a href="https://www.zillow.com">Return to Zillow</a>}
    />
  </Instrument>
);

const RenterAuthGate = ({ children }: { children: JSX.Element }) => {
  return (
    <RenterAuth.Boundary
      loading={<></>}
      redirecting={<></>}
      redirectHref={countedLoginRedirectHref}
      onError={<RenterAuthError />}
    >
      {(user) =>
        isRenterBlocked(user) ? (
          <Instrument
            onInitialRender={() =>
              getGlobalMonitor()?.count({
                name: 'rent-payment-web renter auth-react migration blocked_user',
              })
            }
          >
            <RenterBlockedUser />
          </Instrument>
        ) : (
          <Instrument
            onInitialRender={() =>
              getGlobalMonitor()?.count({
                name: 'rent-payment-web renter auth-react migration authorized',
              })
            }
          >
            {children}
          </Instrument>
        )
      }
    </RenterAuth.Boundary>
  );
};

export const RenterAuthBoundary = ({ exact, path, render }: RouteWithAuthProps) => {
  return (
    <Route
      exact={exact}
      path={path}
      render={(routeProps) => <RenterAuthGate>{render({ ...routeProps, loading: true })}</RenterAuthGate>}
    />
  );
};
